import { useShipmentsApi } from "@/Hooks/useShipmentsApi";
import { LocationsRestrictedDockHoursInput } from "@/Shared/Locations/Inputs/LocationsRestrictedDockHoursInput";
import { locationsErrorMessageForHours } from "@/Shared/Locations/Validators/locationsErrorMessageForHours";
import { getRestrictionViolation } from "@/Shared/Locations/Helpers/locationsHoursOptions";
import { changeDeliveryHoursConfirmationMessage } from "@/Features/ChangeDeliveryHours/Helpers/changeDeliveryHoursConfirmationMessage";
import { changeDeliveryHoursIsUnchanged } from "@/Features/ChangeDeliveryHours/Helpers/changeDeliveryHoursIsUnchanged";
import type { DialogBaseProps } from "@freightsimple/ui-core/Dialog/DialogBaseProps";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { DialogResult } from "@freightsimple/ui-core/Dialog/DialogResult";
import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { useState } from "react";

interface ChangeDeliveryHoursDialogProps extends DialogBaseProps {
  shipment: Shipment;
}

export function ChangeDeliveryHoursDialog(
  props: ChangeDeliveryHoursDialogProps,
) {
  const [processing, setProcessing] = useState(false);
  const [openFrom, setOpenFrom] = useState(
    props.shipment.deliveryLocation.hours.openFrom,
  );
  const [openUntil, setOpenUntil] = useState(
    props.shipment.deliveryLocation.hours.openUntil,
  );
  const [forceValidation, setForceValidation] = useState(false);
  // The success/error notice rides on top of the hours dialog, exactly like
  // the legacy alert modal stacked on the legacy hours modal.
  const [notice, setNotice] = useState<
    { title: string; message: string; closeOnDismiss: boolean } | undefined
  >();
  const shipmentsApi = useShipmentsApi();

  const allowAfterHours = props.shipment.deliveryLocation.accessorials.includes(
    "SCHEDULING_AFTER_HOURS",
  );

  const hoursErrorMessage = locationsErrorMessageForHours(
    { openFrom, openUntil },
    allowAfterHours,
    false,
  );

  const restrictions = props.shipment.selectedQuote?.restrictions;
  const restrictionError =
    restrictions && restrictions.length > 0
      ? getRestrictionViolation(
          openUntil,
          LocationContext.Delivery,
          "openUntil",
          restrictions,
          undefined,
        )
      : undefined;

  async function onConfirm(): Promise<DialogResult> {
    if (hoursErrorMessage !== undefined || restrictionError !== undefined) {
      setForceValidation(true);
      return DialogResult.KeepOpen;
    }

    if (
      changeDeliveryHoursIsUnchanged(props.shipment.deliveryLocation.hours, {
        openFrom,
        openUntil,
      })
    ) {
      setNotice({
        title: "No change",
        message:
          "These are already the delivery hours on your shipment, so nothing was changed.",
        closeOnDismiss: false,
      });
      return DialogResult.KeepOpen;
    }

    setProcessing(true);
    try {
      const response = await shipmentsApi.requestDeliveryHoursChange({
        requestDeliveryHoursChangeRequest: {
          shipmentId: props.shipment.shipmentId,
          newDeliveryHours: {
            openFrom,
            openUntil,
          },
        },
      });
      setProcessing(false);
      setNotice({
        title: "Received",
        message: changeDeliveryHoursConfirmationMessage(
          props.shipment.shipmentState,
          response.carrierEmailed,
        ),
        closeOnDismiss: true,
      });
    } catch {
      setProcessing(false);
      setNotice({
        title: "Error",
        message: "Sorry, something went wrong. Our team has been alerted.",
        closeOnDismiss: false,
      });
    }
    return DialogResult.KeepOpen;
  }

  function dismissNotice() {
    const closeAfter = notice?.closeOnDismiss;
    setNotice(undefined);
    if (closeAfter) {
      props.onOpenChange?.(false);
    }
  }

  return (
    <>
      <Dialog
        open={props.open}
        onOpenChange={props.onOpenChange}
        title="Change Delivery Hours"
        description="Select the updated times when your shipment can be delivered."
        width="medium"
        okText="Change Delivery Hours"
        okLoading={processing}
        onOk={onConfirm}
      >
        <ControlPresentationProvider presentation="underlined">
          <LocationsRestrictedDockHoursInput
            openFrom={openFrom}
            openUntil={openUntil}
            setOpenFrom={setOpenFrom}
            setOpenUntil={setOpenUntil}
            context={LocationContext.Delivery}
            restrictions={props.shipment.selectedQuote?.restrictions}
            pickupOpenUntil={undefined}
            hoursErrorMessage={hoursErrorMessage}
            forceValidation={forceValidation}
            warningMessage={undefined}
          />
        </ControlPresentationProvider>
      </Dialog>
      <Dialog
        open={notice !== undefined}
        onOpenChange={(open) => {
          if (!open) dismissNotice();
        }}
        title={notice?.title}
        footer={
          <Button primary onClick={dismissNotice}>
            OK
          </Button>
        }
      >
        <ControlPresentationProvider presentation="underlined">
          {notice?.message}
        </ControlPresentationProvider>
      </Dialog>
    </>
  );
}
